import { useRef, useState } from "react";
import { AlertTriangle, Check, Phone, Send } from "lucide-react";
import { postContact } from "../lib/api";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];

const LIMITS = { name: 80, subject: 120, message: 2000, phone: 30 };

const field =
  "mt-2 h-12 w-full rounded-xl border border-sand-border bg-sand px-4 text-sm font-medium text-navy outline-none transition-colors focus:border-lake focus:bg-white";
const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

const validate = (v) => {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Please tell us your name.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = "That email address doesn't look right.";
  if (v.subject.trim().length < 3) e.subject = "Add a short subject.";
  if (v.message.trim().length < 10) e.message = "A little more detail helps us answer properly.";
  return e;
};

export const ContactForm = () => {
  const openedAt = useRef(Date.now());
  const [values, setValues] = useState({
    name: "", email: "", phone: "", location: "general", subject: "", message: "", company: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) return;

    setStatus({ state: "sending", message: "" });
    try {
      const res = await postContact({ ...values, elapsedMs: Date.now() - openedAt.current });
      setStatus({
        state: "sent",
        message: res.acknowledgementSent === false
          ? "Your message reached the marina. We couldn't send you a copy, but we have it."
          : "Your message is on its way — we've emailed you a copy too.",
      });
      setValues({ name: "", email: "", phone: "", location: "general", subject: "", message: "", company: "" });
      openedAt.current = Date.now();
    } catch (err) {
      setStatus({
        state: "error",
        message: err.payload?.userMessage || "We couldn't send your message just now.",
      });
    }
  };

  if (status.state === "sent") {
    return (
      <div
        data-testid="contact-form-success"
        className="rounded-3xl border border-lake/30 bg-lake/[0.06] p-8 text-center"
      >
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-lake/15">
          <Check className="h-6 w-6 text-lake" aria-hidden="true" />
        </span>
        <p className="mt-4 font-display text-xl font-bold text-navy">Message sent</p>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-700">{status.message}</p>
        <button
          type="button"
          data-testid="contact-form-send-another"
          onClick={() => setStatus({ state: "idle", message: "" })}
          className="mt-6 text-sm font-bold text-lake hover:underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      data-testid="contact-form"
      className="rounded-3xl border border-sand-border bg-white p-7 lg:p-9"
    >
      <h2 className="font-display text-2xl font-bold text-navy">Send us a message</h2>
      <p className="mt-2 text-sm text-slate-600">
        We answer during marina hours. For anything happening today, please call — it's faster.
      </p>

      {/* honeypot: hidden from people, irresistible to bots */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="cf-company">Company</label>
        <input id="cf-company" type="text" tabIndex={-1} autoComplete="off" value={values.company} onChange={set("company")} />
      </div>

      <div className="mt-7 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className={labelCls}>Your name</label>
          <input id="cf-name" data-testid="contact-input-name" className={field} value={values.name}
                 maxLength={LIMITS.name} onChange={set("name")} autoComplete="name" />
          {errors.name && <p data-testid="contact-error-name" className="mt-1.5 text-xs font-semibold text-ember">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="cf-email" className={labelCls}>Email</label>
          <input id="cf-email" type="email" data-testid="contact-input-email" className={field} value={values.email}
                 onChange={set("email")} autoComplete="email" />
          {errors.email && <p data-testid="contact-error-email" className="mt-1.5 text-xs font-semibold text-ember">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="cf-phone" className={labelCls}>Phone <span className="font-medium normal-case text-slate-400">(optional)</span></label>
          <input id="cf-phone" data-testid="contact-input-phone" className={field} value={values.phone}
                 maxLength={LIMITS.phone} onChange={set("phone")} autoComplete="tel" />
        </div>
        <div>
          <label htmlFor="cf-location" className={labelCls}>Which location</label>
          <select id="cf-location" data-testid="contact-input-location" className={field} value={values.location}
                  onChange={set("location")}>
            <option value="general">General enquiry</option>
            <option value="west-branch">West Branch</option>
            <option value="lake-milton">Lake Milton</option>
          </select>
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="cf-subject" className={labelCls}>Subject</label>
        <input id="cf-subject" data-testid="contact-input-subject" className={field} value={values.subject}
               maxLength={LIMITS.subject} onChange={set("subject")} />
        {errors.subject && <p data-testid="contact-error-subject" className="mt-1.5 text-xs font-semibold text-ember">{errors.subject}</p>}
      </div>

      <div className="mt-5">
        <label htmlFor="cf-message" className={labelCls}>Message</label>
        <textarea id="cf-message" rows={6} data-testid="contact-input-message" value={values.message}
                  maxLength={LIMITS.message} onChange={set("message")}
                  className="mt-2 w-full rounded-xl border border-sand-border bg-sand p-4 text-sm font-medium leading-relaxed text-navy outline-none transition-colors focus:border-lake focus:bg-white" />
        <div className="mt-1.5 flex items-center justify-between gap-4">
          {errors.message
            ? <p data-testid="contact-error-message" className="text-xs font-semibold text-ember">{errors.message}</p>
            : <span />}
          <span className="text-xs text-slate-400">{values.message.length}/{LIMITS.message}</span>
        </div>
      </div>

      {status.state === "error" && (
        <div data-testid="contact-form-error" role="alert"
             className="mt-6 flex flex-col gap-3 rounded-xl border border-ember/40 bg-ember/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2 text-sm text-navy">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-ember" aria-hidden="true" />
            {status.message}
          </p>
          <a href={WB.phoneHref} data-testid="contact-form-error-call-link"
             className="inline-flex shrink-0 items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white">
            <Phone className="h-4 w-4" aria-hidden="true" />
            Call {WB.phone}
          </a>
        </div>
      )}

      <button type="submit" data-testid="contact-form-submit" disabled={status.state === "sending"}
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ember px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-ember-hover disabled:opacity-60 sm:w-auto">
        <Send className="h-4 w-4" aria-hidden="true" />
        {status.state === "sending" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
};

export default ContactForm;
